import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { EasyCheckButton, EasyCheckDatePicker, EasyCheckDialog } from '../../common/Controls';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import { formatCurrencyAmount } from '../../../utils/currencyUtils';
import PropTypes from 'prop-types';

const ClearingDialog = ({ open, onClose, cheques, onConfirm, loading }) => {
  const [clearedDate, setClearedDate] = useState(new Date());
  const [dateError, setDateError] = useState('');

  const totalAmount = cheques.reduce((sum, c) => sum + (parseFloat(c.chqAmount) || 0), 0);
  const currency = cheques[0]?.Currency || cheques[0]?.currency || 'AED';

  const handleDateChange = (newDate) => {
    setClearedDate(newDate);
    if (newDate && newDate > new Date()) {
      setDateError('Cleared date cannot be in the future');
    } else {
      setDateError('');
    }
  };

  const handleConfirm = () => {
    if (!clearedDate) {
      setDateError('Cleared date is required');
      return;
    }
    if (clearedDate > new Date()) {
      setDateError('Cleared date cannot be in the future');
      return;
    }
    onConfirm(clearedDate);
  };

  const isValid = clearedDate && !dateError;

  return (
    <EasyCheckDialog
      open={open}
      onClose={onClose}
      title="Mark as Cleared"
      titleIcon={<CheckCircleIcon />}
      variant="form"
      maxWidth="sm"
      actions={
        <>
          <EasyCheckButton buttonType="form" variant="outlined" onClick={onClose}>
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            buttonType="form"
            color="success"
            onClick={handleConfirm}
            disabled={!isValid}
            loading={loading}
          >
            Confirm
          </EasyCheckButton>
        </>
      }
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {cheques.length} cheque(s) selected — Total: {formatCurrencyAmount(totalAmount, currency, { withSymbol: false })}
      </Typography>

      {cheques.length <= 10 && (
        <TableContainer sx={{ mb: 2, maxHeight: 200 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Cheque #</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Beneficiary</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cheques.map((cheque) => (
                <TableRow key={cheque.id}>
                  <TableCell sx={{ py: 0.5, fontFamily: 'monospace' }}>{cheque.chequeNumber}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{cheque.beneficiaryName}</TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    {formatCurrencyAmount(cheque.chqAmount, cheque.Currency || cheque.currency || currency, { withSymbol: false })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EasyCheckDatePicker
        value={clearedDate}
        onChange={handleDateChange}
        datePickerVariant="form"
        label="Cleared Date *"
        format="dd-MM-yyyy"
        maxDate={new Date()}
        error={!!dateError}
        helperText={dateError || 'Date the cheque was cleared by the bank'}
        fullWidth
      />
    </EasyCheckDialog>
  );
};

ClearingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cheques: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default ClearingDialog;
